/**
 * Production Calendar — Business Central OData layer + working-day algorithm.
 *
 * Confirmed duplicate logic between Ops Hub (apps/production-calendar/bcCalendarApi.js)
 * and Sales Hub (api/bc.js's fetchBcProductionDays/fetchRawCalendarRows) — same BC
 * entities, same Nonworking-flag semantics, same working-day walk. This module is
 * the single source of truth both apps now delegate to.
 *
 * BC-fetching functions take `bcFetch` as their first argument rather than importing
 * one — each app supplies its own instance (bound to its own MSAL auth), same
 * pattern as bcApi.js's createBcClient(). With no `bcApi` param, bcFetch targets
 * the OData V4 Web Services endpoint:
 *   .../v2.0/{tenant}/Production/ODataV4/Company('A.H. Beard Pty Ltd (AU)')/{entity}
 *
 * Business rules:
 *   - Production runs Mon–Fri, excluding days explicitly marked closed in BC.
 *   - Base_Calendar_Change_List_Excel: dense table with a row per weekday, each
 *     carrying a Nonworking flag (true = closed, false = explicitly open). Only
 *     rows with Nonworking === true are closed days; a weekday with a false row —
 *     or no row at all — is open. (Customized_Cal_Change_Excel was abandoned —
 *     do not use it.)
 *   - Next Production = next open working day strictly after today.
 *   - Next Dispatch  = production + 2 working days (3rd open working day from today).
 *
 * Date handling (learned the hard way):
 *   - Always LOCAL date, never UTC — toISOString() shows Australian users yesterday.
 *   - Parse YYYY-MM-DD with a "T00:00:00" suffix so the TZ doesn't shift it back a day.
 */

// ─── LOCAL DATE HELPERS ───────────────────────────────────────────────────────

/** YYYY-MM-DD in LOCAL time (never UTC). */
export function localDateStr(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Parse a YYYY-MM-DD string to a local Date (midnight), avoiding the UTC shift. */
export function parseLocal(ds) {
  return new Date(ds + "T00:00:00");
}

/** Whole calendar days between two YYYY-MM-DD strings (to − from). */
export function calendarDaysBetween(fromStr, toStr) {
  return Math.round((parseLocal(toStr) - parseLocal(fromStr)) / 86_400_000);
}

/** en-AU short label, e.g. "Tue 17 Jun". */
export function fmtDayLabel(ds) {
  if (!ds) return "—";
  return parseLocal(ds).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
}

const isWeekend = (dow) => dow === 0 || dow === 6;

/** Truthy across OData boolean encodings (true / "true" / 1). */
export const isNonworking = (v) => v === true || v === "true" || v === 1 || v === "1";

// ─── BC FETCHERS ──────────────────────────────────────────────────────────────

/** Production calendars only (filtered on the custom ProductionCalendar flag). */
export async function fetchProductionCalendars(bcFetch) {
  const rows = await bcFetch("Base_Calendar_Card_Excel", {
    "$filter": "ProductionCalendar eq true",
    "$select": "Code,Name",
    maxPages:  "5",
  });
  return rows.map(r => ({ code: r.Code, name: r.Name || r.Code }));
}

/**
 * Raw calendar-change rows for a calendar from `fromDateStr` onward (inclusive),
 * unfiltered — callers that need the raw Nonworking flag per row (e.g. rendering
 * a calendar grid) should use this; callers that just need the set of closed
 * dates should use `fetchClosedDays` below.
 */
export async function fetchClosedDayRows(bcFetch, code, fromDateStr, { top = 50 } = {}) {
  const safeCode = code.replace(/'/g, "''");
  return bcFetch("Base_Calendar_Change_List_Excel", {
    "$filter":  `Base_Calendar_Code eq '${safeCode}' and Date ge ${fromDateStr}`,
    "$orderby": "Date asc",
    "$top":     String(top),
    "$select":  "Date,Nonworking",
    maxPages:   "5",
  });
}

/**
 * Closed days for a calendar, from `fromDateStr` onward (inclusive).
 * Returns an array of YYYY-MM-DD strings.
 */
export async function fetchClosedDays(bcFetch, code, fromDateStr, opts) {
  const rows = await fetchClosedDayRows(bcFetch, code, fromDateStr, opts);
  return rows
    .filter(r => isNonworking(r.Nonworking))
    .map(r => String(r.Date).slice(0, 10));
}

// ─── WORKING-DAY ALGORITHM ─────────────────────────────────────────────────────

/**
 * Walk forward from tomorrow collecting open working days.
 * openDays[0] = Next Production, openDays[2] = Next Dispatch (prod + 2 working days).
 */
export function computeSchedule(closedSet) {
  const cursor = parseLocal(localDateStr());
  const openDays = [];
  for (let i = 0; i < 90 && openDays.length < 3; i++) {
    cursor.setDate(cursor.getDate() + 1);            // start tomorrow (strictly after today)
    const ds = localDateStr(cursor);
    if (!isWeekend(cursor.getDay()) && !closedSet.has(ds)) openDays.push(ds);
  }
  return { nextProduction: openDays[0] || null, nextDispatch: openDays[2] || null };
}

/** Mon–Fri count from tomorrow through `targetStr` inclusive; 0 if target ≤ today. */
export function countWorkingDaysUntil(targetStr) {
  if (!targetStr) return 0;
  const today  = parseLocal(localDateStr());
  const target = parseLocal(targetStr);
  if (target <= today) return 0;
  let count = 0;
  const cur = new Date(today);
  for (let i = 0; i < 120; i++) {
    cur.setDate(cur.getDate() + 1);
    if (!isWeekend(cur.getDay())) count++;
    if (localDateStr(cur) === targetStr) break;
  }
  return count;
}

/** Dispatch for a given start day: skip weekends + closed days, stop after 2 open days. */
export function dispatchFor(startStr, closedSet) {
  const cur = parseLocal(startStr);
  let open = 0;
  for (let i = 0; i < 90; i++) {
    cur.setDate(cur.getDate() + 1);
    const ds = localDateStr(cur);
    if (!isWeekend(cur.getDay()) && !closedSet.has(ds)) {
      open++;
      if (open === 2) return ds;
    }
  }
  return null;
}

/**
 * Status + dispatch for the next 30 WORKING days from today (modal view).
 * Weekends are skipped so the list stays compact and reaches ~6 weeks ahead —
 * enough to reveal locations that close their production calendar early.
 */
export function build30WorkingDayView(closedSet) {
  const d = parseLocal(localDateStr());
  const out = [];
  // Cap the calendar-day scan so a fully-closed calendar can't loop forever.
  for (let scanned = 0; out.length < 30 && scanned < 90; scanned++) {
    if (!isWeekend(d.getDay())) {
      const ds       = localDateStr(d);
      const closed   = closedSet.has(ds);
      const status   = closed ? "Closed" : "Open";
      const dispatch = status === "Open" ? dispatchFor(ds, closedSet) : null;
      out.push({ ds, status, dispatch });
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}
